import React from 'react';
import * as yup from 'yup';
import { FormikValues } from 'formik';
import { formatShortDate } from 'modules/Common/utilities';
import { FormikCheckboxGroup, FormikTextField } from 'modules/Common/components';
import { RecordDefinition } from 'modules/Pilot/BatchRecord/SingleView/components/RecordTable/RecordTable';
import { BatchRecord, LASER_NUM_MAX, SingulationSubRecord } from 'modules/Pilot/BatchRecord/domain';
import { DataTableRow } from 'modules/Pilot/BatchRecord/SingleView/components/SimpleDataTable/TableData';
import {
  recordSingulation,
  RecordSingulationActions,
  amendSingulation,
  AmendSingulationActions,
} from 'modules/Pilot/BatchRecord/actions';
import { PartStatus } from 'modules/RecordManagement';
import { UserPermission } from 'modules/Core/types';
import getInitialSplits from '../../initialSplits';

interface Values extends FormikValues {
  lasers: number[];
  splits: number[];
  comment?: string;
}

export default (
  batchRecord: BatchRecord,
  itemsBeingEdited: { key: string; itemId: string }[],
  userPermissions: UserPermission[]
): RecordDefinition<Values> => {
  const dialogName = 'pilot/batch-records/SINGULATION';
  const recordId = batchRecord.id;
  const { splits } = batchRecord.batchRequest;
  const { status, subRecords, requiredPermissions } = batchRecord.postDrying.singulation;
  const itemBeingEdited = itemsBeingEdited.find(
    item =>
      item.key === `${dialogName}_EDIT` &&
      subRecords &&
      subRecords.findIndex(print => print.id === item.itemId) > -1
  );

  const rowForEditing = itemBeingEdited
    ? subRecords.find(print => print.id === itemBeingEdited.itemId)
    : null;

  const laserOptions: number[] = [];
  for (let i = 1; i < LASER_NUM_MAX + 1; i += 1) laserOptions.push(i);

  const isAllowedToEdit = () => {
    if (
      status === PartStatus.Locked ||
      status === PartStatus.ReadOnly ||
      status === PartStatus.Blocked
    )
      return false;

    let result = true;
    requiredPermissions?.forEach(req => {
      if (userPermissions.find(perm => perm.name === req) === undefined) result = false;
    });

    return result;
  };

  const newSubmit = (values: Values, _, dispatch) =>
    dispatch(recordSingulation(recordId, { ...values }));
  const editSubmit = (values: Values, _, dispatch) =>
    dispatch(amendSingulation(recordId, { ...values, subRecordId: rowForEditing?.id }));

  const validation = {
    splits: yup
      .array()
      .of(yup.number().defined())
      .defined()
      .min(1)
      .max(splits.length),
    lasers: yup
      .array()
      .of(yup.number().defined())
      .defined()
      .min(1)
      .max(LASER_NUM_MAX),
  };

  const editValidation = {
    ...validation,
    comment: yup
      .string()
      .defined()
      .required()
      .min(5),
  };

  const createDataTableRow = (row: SingulationSubRecord): DataTableRow => ({
    key: row.id,
    fields: {
      splits: { value: row.splits.join(', ') },
      lasers: { value: row.lasers.join(', ') },
      performedBy: { user: row.performedBy.user.name, date: formatShortDate(row.performedBy.date) },
    },
  });

  return {
    title: 'Singulation',
    tableData: {
      columns: [
        { accessor: 'splits', label: 'Split(s)' },
        { accessor: 'lasers', label: 'Laser Number(s)' },
        { accessor: 'performedBy', label: 'Performed by', isSignOff: true },
      ],
      rows: subRecords ? subRecords.map(row => createDataTableRow(row)) : [],
    },
    canAddRow: status === PartStatus.Incomplete || status === PartStatus.Complete,
    canEditRows: isAllowedToEdit(),
    formDefinition: {
      title: 'Singulation',
      dialogName,
      apiAction: rowForEditing ? AmendSingulationActions.CALL : RecordSingulationActions.CALL,
      onSubmit: rowForEditing ? editSubmit : newSubmit,
      initialValues: {
        splits: getInitialSplits(rowForEditing, splits),
        lasers: rowForEditing ? rowForEditing.lasers : [],
      },
      form: () => (
        <>
          <FormikCheckboxGroup
            name="splits"
            label="Splits"
            options={splits}
            labelPlacement="top"
            allowSelectAll
          />
          <br />
          <FormikCheckboxGroup
            name="lasers"
            label="Laser Numbers"
            options={laserOptions}
            labelPlacement="top"
          />
          {rowForEditing && <FormikTextField name="comment" label="Reason for change" multiline />}
        </>
      ),
      validationSchema: (): yup.ObjectSchema<Values> =>
        yup
          .object()
          .shape(rowForEditing ? editValidation : validation)
          .defined(),
    },
  };
};
